import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  InputNumber,
  Form,
  Checkbox,
  Card,
  Collapse,
  Avatar,
  Space,
  Switch,
  Radio,
  Select,
  Tabs,
  TimePicker
} from "antd";
import moment from "moment";
import IntlMessages from "@iso/components/utility/intlMessages";
import ConfigureModalSettingWrapper from "./configureModal.style";
import { alarmCategories } from "@iso/constants/alarmCategories";
import { device } from "../../../services/device.service";
import {
  emergencyResponseProviders,
  emergencyResponseTemplates,
} from "../../../services/emergencyResponse.service";
import { userService } from "@iso/services";
import {
  UserOutlined,
  BellOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";

const { Panel } = Collapse;

const ConfigureModal = (props) => {
  const [form] = Form.useForm();
  const [smsAlertsEnabled, setSMSAlertsEnabled] = useState(false);
  const [sosAlarms, setSOSAlarms] = useState("");
  const [tracking, setTracking] = useState("enable");
  const [deviceTime, setDeviceTime] = useState(0);
  const [areaBufferValue, setAreaBufferValue] = useState(0);
  const [usersProperty, setUsersProperty] = useState([]);
  const [activeTab, setActiveTab] = useState("property");
  const [afterHoursUsers, setAfterHoursUsers] = useState([]);
  const [isGroup , setIsGroup] = useState(false);
  const [postAlarmUsers, setPostAlarmUsers] = useState([]);
  const [emergencyProviderId, setEmergencyProviderId] = useState(null);
  const [emergencyTemplateId, setEmergencyTemplateId] = useState(null);
  const [emergencyProviders, setEmergencyProviders] = useState([]);
  const [emergencyTemplates, setEmergencyTemplates] = useState([]);
  // The "Phone" (AI voice) alarm medium is only offered when the backend's
  // n8n webhook is configured (phone_alarm_enabled), so admins can't opt users
  // into a channel that would silently no-op.
  const phoneAlarmEnabled = !!props.assignedDevice?.phone_alarm_enabled;
  // Device-level Phone Call alarm: one call to a fixed number whenever this
  // device raises an alarm, alongside the per-user channels. Off by default.
  const [phoneCallAlarmEnabled, setPhoneCallAlarmEnabled] = useState(false);
  const [phoneCallAlarmNumber, setPhoneCallAlarmNumber] = useState("");
  const [phoneCallAlarmError, setPhoneCallAlarmError] = useState(null);
  // Only a property owner/admin may change it (enforced by the API too).
  const canManagePhoneCallAlarm = !!props.assignedDevice?.can_manage_phone_call_alarm;
  // Load device data
  useEffect(() => {
    if (props.assignedDevice) {
      const {
        device_time,
        sms_alerts_enabled_at,
        sos_alarm,
        tracking_enabled_at,
        area_buffer_value,
        is_group_device,
        device_group_users,
        device_groups,
        after_hours_settings,
        post_alarm_users,
        emergency_response_provider_id,
        emergency_response_template_id,
        phone_call_alarm_enabled,
        phone_call_alarm_number,
      } = props.assignedDevice;
      setSMSAlertsEnabled(!!sms_alerts_enabled_at);
      setSOSAlarms(sos_alarm || "");
      setTracking(tracking_enabled_at ? "enable" : "disable");
      setDeviceTime(device_time || 0);
      setAreaBufferValue(area_buffer_value || 0);
      const groupsWithUsers = device_groups?.map(group => {
        const usersForGroup = device_group_users
          .filter(u => u.group_id === group.id)
          .map(u => ({
            id: u.id,
            user_id: u.user_id,
            notify_email: u.notify_email,
            notify_sms: u.notify_sms,
            notify_push: u.notify_push,
            notify_phone: u.notify_phone
          }));
        return {
          ...group,
          id: group.id,
          interval_time: group.interval_time || 0,
          users: usersForGroup
        };
      });
      setActiveTab(is_group_device ? "deviceGroups" : "property");
      const afterHours = (after_hours_settings && after_hours_settings.length > 0)
        ? after_hours_settings[0]
        : {};

      setAfterHoursUsers(afterHours.users || []);
      setIsGroup(is_group_device);
      setPostAlarmUsers(
        (post_alarm_users || []).map((u) => ({
          id: u.id,
          user_id: u.user_id,
          notify_email: u.notify_email,
        }))
      );
      setEmergencyProviderId(emergency_response_provider_id || null);
      setEmergencyTemplateId(emergency_response_template_id || null);
      setPhoneCallAlarmEnabled(!!phone_call_alarm_enabled);
      setPhoneCallAlarmNumber(phone_call_alarm_number || "");
      setPhoneCallAlarmError(null);
      form.setFieldsValue({
        enableSMSAlerts: !!sms_alerts_enabled_at,
        sosAlarm: sos_alarm || "",
        tracking: tracking_enabled_at ? "enable" : "disable",
        deviceTime: device_time || 0,
        areaBufferValue: area_buffer_value || 0,
        deviceGroups: groupsWithUsers,
        is_group_device: is_group_device,
        afterHours: {
          start_time: afterHours.start_time
            ? moment(afterHours.start_time, "HH:mm:ss")
            : null,
          end_time: afterHours.end_time
            ? moment(afterHours.end_time, "HH:mm:ss")
            : null,
        }
      });
    }
  }, [props.assignedDevice, form]);

  const cancelModal = () => {
    form.resetFields();
    setSMSAlertsEnabled(false);
    setSOSAlarms("");
    setTracking("enable");
    setDeviceTime(0);
    setAreaBufferValue(0);
    setPostAlarmUsers([]);
    setPhoneCallAlarmEnabled(false);
    setPhoneCallAlarmNumber("");
    setPhoneCallAlarmError(null);
    props?.cancelConfigureModal();
  };

  // The Phone Call tab holds plain state (not Form fields), like the other
  // non-form tabs, so its one rule is checked here before the form validates.
  const validatePhoneCallAlarm = () => {
    const number = phoneCallAlarmNumber.trim();
    if (phoneCallAlarmEnabled && !number) {
      return "Enter the phone number to call when an alarm goes off.";
    }
    if (number && !/^\+?[\d\s()-]{8,20}$/.test(number)) {
      return "Enter a valid phone number, e.g. 0412 345 678 or +61412345678.";
    }
    return null;
  };

  const handleSubmit = () => {
    const phoneCallError = validatePhoneCallAlarm();
    setPhoneCallAlarmError(phoneCallError);
    if (phoneCallError) {
      setActiveTab("phoneCall");
      return;
    }
    form
      .validateFields()
      .then((values) => {
        onStore(values);
      })
      .catch((info) => {
        console.error("Validate Failed:", info);
      });
  };
  const renderGroupTitle = (groupIndex) => {
    const groupUsers = form.getFieldValue(["deviceGroups", groupIndex, "users"]) || [];
    const selectedCount = groupUsers.filter(u => u.notify_email || u.notify_sms || u.notify_push || u.notify_phone).length;
    const totalCount = usersProperty.length; // giả sử usersProperty là danh sách tất cả user có thể chọn
    return `Group #${groupIndex + 1} (${selectedCount}/${totalCount} users)`;
  };
  const onStore = (values) => {
    const smsAlert = smsAlertsEnabled
      ? moment().format("YYYY-MM-DD HH:mm:ss")
      : "";
    const trackingTime =
      tracking === "enable" ? moment().format("YYYY-MM-DD HH:mm:ss") : "";
    const groups =
      isGroup
        ? Object.entries(values.deviceGroups || {}).map(([groupKey, groupData]) => ({
          id: groupData.id || null, 
          device_id: props.assignedDevice?.id,
          interval_time: groupData.interval_time || null,
          users: (groupData.users || []).map((u) => ({
            id: u.id || null,
            user_id: u.user_id,
            notify_email: u.notify_email,
            notify_sms: u.notify_sms,
            notify_push: u.notify_push,
            notify_phone: u.notify_phone,
          })),
        }))
      : [];
    const afterHours = {
      device_id: props.assignedDevice?.id,
      start_time: values.afterHours?.start_time?.format("HH:mm") || null,
      end_time: values.afterHours?.end_time?.format("HH:mm") || null,
      users: afterHoursUsers.map(u => ({
        id: u.id || null,
        user_id: u.user_id,
        notify_email: u.notify_email,
        notify_sms: u.notify_sms,
        notify_push: u.notify_push,
        notify_phone: u.notify_phone,
      }))
    };
    const postAlarm = {
      device_id: props.assignedDevice?.id,
      users: postAlarmUsers.map((u) => ({
        user_id: u.user_id,
        notify_email: u.notify_email ? 1 : 0,
      })),
    };
    const data = {
      sms_alerts_enabled_at: smsAlert,
      tracking_enabled_at: trackingTime,
      device_time: values.deviceTime,
      sos_alarm: sosAlarms,
      user_id: props?.assignedDevice?.user_id,
      area_buffer_value: areaBufferValue,
      is_group_device: isGroup ? 1 : 0,
      active_tab_setting: activeTab,
      emergency_response_provider_id: emergencyProviderId || null,
      emergency_response_template_id: emergencyTemplateId || null,
      phone_call_alarm_enabled: phoneCallAlarmEnabled ? 1 : 0,
      phone_call_alarm_number: phoneCallAlarmNumber.trim(),
      deviceGroups: {
        device_id: props.assignedDevice?.id,
        groups,
      },
      afterHours: afterHours,
      postAlarm: postAlarm,
    };
    device.storeOrUpdate(data, props?.assignedDevice?.id).then((res) => {
      if (res.code === "0000") {
        form.resetFields();
        props.getDeviceByProperty();
        cancelModal();
      } else if (res.device?.phone_call_alarm_number) {
        // Server-side number validation (it normalises to E.164 before checking).
        setPhoneCallAlarmError(res.device.phone_call_alarm_number[0]);
        setActiveTab("phoneCall");
      }
    });
  };

  // Load users for property
  useEffect(() => {
    if (props.propertyId) {
      getUsers(props.propertyId);
    }
  }, [props.propertyId]);

  // Load Emergency Response providers + templates lazily — only when
  // the user actually opens the Emergency Response tab. Avoids two
  // API calls every time the modal opens for any device on any tab.
  // The `emergencyProviders.length === 0` guard makes this idempotent:
  // subsequent visits to the tab in the same modal session don't
  // re-fetch. Defaulting: if the assigned device has no provider set,
  // pre-select the first active one (Ripper Corp out of the box).
  useEffect(() => {
    if (activeTab !== 'emergencyResponse' || emergencyProviders.length > 0) {
      return;
    }
    emergencyResponseProviders.list({ onlyActive: true }).then((res) => {
      const list = res.providers || [];
      setEmergencyProviders(list);
      setEmergencyProviderId((current) => {
        if (current) return current;
        return list.length > 0 ? list[0].id : null;
      });
    });
    emergencyResponseTemplates.list().then((res) => {
      setEmergencyTemplates(res.templates || []);
    });
  }, [activeTab, emergencyProviders.length]);

  const getUsers = (propertyId) => {
    userService.getUserForProperty(propertyId).then((res) => {
      if (res.code === "0000") {
        setUsersProperty(res.users);
      }
    });
  };

  // Render users for group
  const renderUsersForGroup = (groupName) => {
    return usersProperty.map((user) => (
      <Form.Item key={user.id} noStyle shouldUpdate>
        {() => {
          const currentUsers =
            form.getFieldValue(["deviceGroups", groupName, "users"]) || [];
          const currentUser =
            currentUsers.find((u) => u.user_id === user.id) || {};
          
          const selectedValues = [
            ...(currentUser.notify_email ? ["notify_email"] : []),
            ...(currentUser.notify_sms ? ["notify_sms"] : []),
            ...(currentUser.notify_push ? ["notify_push"] : []),
            ...(currentUser.notify_phone ? ["notify_phone"] : []),
          ];

          const handleChange = (checkedValues) => {
            let newUsers = [...currentUsers];
            const existingUser = newUsers.find((u) => u.user_id === user.id);
            const updatedUser = {
              id: existingUser?.id || null,
              user_id: user.id,
              notify_email: checkedValues.includes("notify_email") ? 1 : 0,
              notify_sms: checkedValues.includes("notify_sms") ? 1 : 0,
              notify_push: checkedValues.includes("notify_push") ? 1 : 0,
              notify_phone: checkedValues.includes("notify_phone") ? 1 : 0,
            };

            const idx = newUsers.findIndex((u) => u.user_id === user.id);
            if (idx > -1) {
              newUsers[idx] = updatedUser;
            } else {
              newUsers.push(updatedUser);
            }

            const currentDeviceGroups = form.getFieldValue("deviceGroups") || [];
            currentDeviceGroups[groupName] = {
              ...(currentDeviceGroups[groupName] || {}),
              users: newUsers,
            };

            form.setFieldsValue({
              deviceGroups: currentDeviceGroups,
            });
          };

          return (
            <Card
              size="small"
              style={{ marginBottom: 8 }}
              className={selectedValues.length ? "active" : ""}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <span>{user.name}</span>
                </Space>
                <Space>
                  <Checkbox.Group
                    options={[
                      { label: "Email", value: "notify_email" },
                      { label: "SMS", value: "notify_sms" },
                      { label: "Push", value: "notify_push" },
                      ...(phoneAlarmEnabled
                        ? [{ label: "Phone", value: "notify_phone" }]
                        : []),
                    ]}
                    value={selectedValues}
                    onChange={handleChange}
                  />
                  {selectedValues.length > 0 && (
                    <BellOutlined style={{ color: "#0B2A1B", marginLeft: 8 }} />
                  )}
                </Space>
              </div>
            </Card>
          );
        }}
      </Form.Item>
    ));
  };

  const renderAfterHoursUsers = () => {
    return usersProperty.map((user) => {
      const currentUser = afterHoursUsers.find(u => u.user_id === user.id) || {};
      const selectedValues = [
        ...(currentUser.notify_email ? ["notify_email"] : []),
        ...(currentUser.notify_sms ? ["notify_sms"] : []),
        ...(currentUser.notify_push ? ["notify_push"] : []),
        ...(currentUser.notify_phone ? ["notify_phone"] : []),
      ];

      const handleChange = (checkedValues) => {
        let newUsers = [...afterHoursUsers];
        const existingUser = newUsers.find((u) => u.user_id === user.id);
        const updatedUser = {
          id: existingUser?.id || null,
          user_id: user.id,
          notify_email: checkedValues.includes("notify_email") ? 1 : 0,
          notify_sms: checkedValues.includes("notify_sms") ? 1 : 0,
          notify_push: checkedValues.includes("notify_push") ? 1 : 0,
          notify_phone: checkedValues.includes("notify_phone") ? 1 : 0,
        };

        const idx = newUsers.findIndex((u) => u.user_id === user.id);
        if (
          updatedUser.notify_email === 0 &&
          updatedUser.notify_sms === 0 &&
          updatedUser.notify_push === 0 &&
          updatedUser.notify_phone === 0
        ) {
          if (idx > -1) {
            newUsers.splice(idx, 1);
          }
        } else {
          if (idx > -1) {
            newUsers[idx] = updatedUser;
          } else {
            newUsers.push(updatedUser);
          }
        }
        setAfterHoursUsers(newUsers);
      };

      return (
        <Card
          key={user.id}
          size="small"
          style={{ marginBottom: 8 }}
          className={selectedValues.length ? "active" : ""}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space>
              <Avatar size="small" icon={<UserOutlined />} />
              <span>{user.name}</span>
            </Space>
            <Space>
              <Checkbox.Group
                options={[
                  { label: "Email", value: "notify_email" },
                  { label: "SMS", value: "notify_sms" },
                  { label: "Push", value: "notify_push" },
                  ...(phoneAlarmEnabled
                    ? [{ label: "Phone", value: "notify_phone" }]
                    : []),
                ]}
                value={selectedValues}
                onChange={handleChange}
              />
              {selectedValues.length > 0 && (
                <BellOutlined style={{ color: "#0B2A1B", marginLeft: 8 }} />
              )}
            </Space>
          </div>
        </Card>
      );
    });
  };
  const renderAfterHoursUserTitle = () => {
    const totalCount = usersProperty.length;
    return `After Hours (${afterHoursUsers.length}/${totalCount} users)`;
  };

  // Post-Alarm Summary — email-only recipient picker.
  // Mirrors the After-Hours card layout (Avatar + name + single checkbox)
  // so the visual language of the alarm-config modal stays consistent.
  const renderPostAlarmUsers = () => {
    return usersProperty.map((user) => {
      const selected = postAlarmUsers.some((u) => u.user_id === user.id);

      const handleChange = (e) => {
        const checked = e.target.checked;
        // Functional setter form — two rapid clicks would otherwise
        // both see the same pre-first-click snapshot of postAlarmUsers
        // and the second click's update would be a no-op.
        if (checked) {
          setPostAlarmUsers((prev) => [
            ...prev.filter((u) => u.user_id !== user.id),
            { user_id: user.id, notify_email: 1 },
          ]);
        } else {
          setPostAlarmUsers((prev) => prev.filter((u) => u.user_id !== user.id));
        }
      };

      return (
        <Card
          key={user.id}
          size="small"
          style={{ marginBottom: 8 }}
          className={selected ? "active" : ""}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space>
              <Avatar size="small" icon={<UserOutlined />} />
              <span>{user.name}</span>
            </Space>
            <Space>
              <Checkbox checked={selected} onChange={handleChange}>Email</Checkbox>
              {selected && <BellOutlined style={{ color: "#0B2A1B", marginLeft: 8 }} />}
            </Space>
          </div>
        </Card>
      );
    });
  };

  const renderPostAlarmTitle = () => {
    const totalCount = usersProperty.length;
    return `Summary Recipients (${postAlarmUsers.length}/${totalCount} users)`;
  };

  return (
    <ConfigureModalSettingWrapper
      forceRender={true}
      visible={props.visibleConfigure}
      width={535}
      title={<IntlMessages id="safeFarmPage.configure.modal.title" />}
      onCancel={cancelModal}
      maskClosable={true}
      footer={[
        <Button key="back" onClick={cancelModal} className="cancel-button">
          <IntlMessages id="propertyPage.modal.cancel" />
        </Button>,
        <Button
          key="submit"
          className="btn-success"
          type="primary"
          onClick={handleSubmit}
        >
          <IntlMessages id="propertyPage.modal.save" />
        </Button>,
      ]}
    >
      <Form form={form} layout="horizontal">
      

        {/* SOS Alarm */}
        <div className="sos-alarm-settings">
          <p className="normal-label">
            <IntlMessages id="configuration.sosAlarmSetting.title" />
          </p>
          <Form.Item name="sosAlarm">
            <Radio.Group
              onChange={(e) => setSOSAlarms(e.target.value)}
              value={sosAlarms}
            >
              {Object.keys(alarmCategories).map((category) => (
                <Radio key={category} value={category}>
                  {alarmCategories[category].label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </div>

        {/* Tracking */}
        <div className="tracking-settings">
          <p className="normal-label">
            <IntlMessages id="Tracking" />
          </p>
          <Form.Item name="tracking">
            <Radio.Group
              onChange={(e) => setTracking(e.target.value)}
              value={tracking}
            >
              <Radio value="enable">Enable Tracking</Radio>
              <Radio value="disable">Disable Tracking</Radio>
            </Radio.Group>
          </Form.Item>
        </div>

        {/* Device Time */}
        <div className="device_time">
          <p className="normal-label">
            <IntlMessages id="configuration.deviceTime.lable" />:
          </p>
          <Form.Item name="deviceTime" className="isoInputWrapper">
            <InputNumber
              value={deviceTime}
              onChange={setDeviceTime}
              addonAfter="Minutes"
              style={{ width: "220px" }}
              size="large"
            />
          </Form.Item>
        </div>

        {/* Area Buffer */}
        <div className="area_buffer">
          <p className="normal-label">
            <IntlMessages id="configuration.areaBuffer.lable" />:
          </p>
          <Form.Item name="areaBufferValue" className="isoInputWrapper">
            <InputNumber
              value={areaBufferValue}
              onChange={setAreaBufferValue}
              addonAfter="Meters (m)"
              style={{ width: "220px" }}
              size="large"
            />
          </Form.Item>
        </div>
        <Form.Item name="is_group_device" className="isoInputWrapper" valuePropName="checked" style={{ marginBottom: 4 }}>
            <Checkbox onChange={(e) => setIsGroup(e.target.checked)}>
              Use notification groups
            </Checkbox>
        </Form.Item>
        <p style={{ marginTop: 0, marginBottom: 16, color: "#6b7280", fontSize: 12, lineHeight: 1.4 }}>
          {isGroup
            ? "ON — alarms notify only the users and channels configured in the Group tab below."
            : "OFF — default behaviour: alarms send Email + Push to all property users. Turn this on to restrict alarms to specific users and channels (Email, SMS, Push, Phone) via the Group tab."}
        </p>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="Property" key="property">
            {/* SMS Alerts */}
            <Form.Item
              name="enableSMSAlerts"
              label={<IntlMessages id="configuration.smsAlertPLD.lable" />}
              valuePropName="checked"
              className="alert-cust"
            >
              <Switch
                onChange={(checked) => setSMSAlertsEnabled(checked)}
                checked={smsAlertsEnabled}
              />
            </Form.Item>
          </Tabs.TabPane>
          {/* Device Groups */}
          <Tabs.TabPane tab="Group" key="deviceGroups">
            <div className="device-group">
              <p className="normal-label">
                <IntlMessages id="configuration.deviceGroup.title" />
              </p>
              <Form.List
                name="deviceGroups"
              >
                {(fields, { add, remove }) => (
                  <>
                    <Collapse accordion>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <Collapse.Panel 
                          key={key} 
                          header={renderGroupTitle (index)} // `Group #${index + 1}`}
                          extra={
                            <Button
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={(e) => {
                                remove(name);
                              }}
                            />
                          }
                        >
                          {index > 0 ? (
                            <Form.Item
                              {...restField}
                              name={[name, "interval_time"]}
                              label="Interval Time"
                              rules={[{ required: true, message: "Please Input Interval" }]}
                            >
                              <InputNumber min={1} />
                            </Form.Item>
                          ) : (
                            // group 1
                            <Form.Item name={[name, "interval_time"]} hidden initialValue={0}>
                              <InputNumber />
                            </Form.Item>
                          )}

                          {/* Users */}
                          {renderUsersForGroup(name)}
                      
                        </Collapse.Panel>
                      ))}
                    </Collapse>
                    <Button
                        type="dashed"
                        onClick={() => add({ interval_time: 0, users: [] })}
                        icon={<PlusOutlined />}
                        style ={{ marginTop: 10}}
                      >
                        <IntlMessages id="configuration.deviceGroup.add" />
                    </Button>
                  </>
                )}
              </Form.List>
            </div>
          </Tabs.TabPane>
          {/* After Hours */}
          <Tabs.TabPane tab="After Hours" key="afterHours">
            <div className="after-hours-settings">
              <div style={{ display: "flex", gap: 16 }}>
                <Form.Item
                  label="Start Time"
                  name={["afterHours", "start_time"]}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <TimePicker format="HH:mm" style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item
                  label="End Time"
                  name={["afterHours", "end_time"]}
                  labelCol={{ span: 24 }} 
                  wrapperCol={{ span: 24 }}
                >
                  <TimePicker format="HH:mm" style={{ width: "100%" }} />
                </Form.Item>
              </div>

              <Collapse accordion>
                <Collapse.Panel header={renderAfterHoursUserTitle()}>
                  {renderAfterHoursUsers()}
                </Collapse.Panel>
              </Collapse>
            </div>
          </Tabs.TabPane>
          {/* Phone Call alarm */}
          <Tabs.TabPane tab="Phone Call" key="phoneCall">
            <div className="phone-call-alarm-settings">
              <p className="normal-label">
                Call a phone number when this device raises an alarm (SOS or
                Safety Alarm). The call is placed once per alarm, in addition
                to the Email, SMS and Push alerts.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <Switch
                  checked={phoneCallAlarmEnabled}
                  disabled={!canManagePhoneCallAlarm}
                  onChange={(checked) => {
                    setPhoneCallAlarmEnabled(checked);
                    setPhoneCallAlarmError(null);
                  }}
                />
                <span>Enable phone call alarm</span>
              </div>
              <Form.Item
                label="Phone number"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                validateStatus={phoneCallAlarmError ? "error" : undefined}
                help={phoneCallAlarmError || undefined}
              >
                <Input
                  size="large"
                  placeholder="e.g. 0412 345 678 or +61412345678"
                  value={phoneCallAlarmNumber}
                  disabled={!canManagePhoneCallAlarm || !phoneCallAlarmEnabled}
                  onChange={(e) => {
                    setPhoneCallAlarmNumber(e.target.value);
                    setPhoneCallAlarmError(null);
                  }}
                />
              </Form.Item>
              {!canManagePhoneCallAlarm && (
                <p className="normal-label" style={{ color: "#6b7280", fontSize: 12 }}>
                  Only a property owner or admin can change the phone call alarm.
                </p>
              )}
              {!phoneAlarmEnabled && (
                <p className="normal-label" style={{ color: "#b45309", fontSize: 12 }}>
                  Phone calls are not configured on this server yet, so no call
                  will be placed until they are.
                </p>
              )}
            </div>
          </Tabs.TabPane>
          {/* Post Alarm */}
          <Tabs.TabPane tab="Post Alarm" key="postAlarm">
            <div className="post-alarm-settings">
              <p className="normal-label">
                Send a post-alarm summary email after this device's alarm is cancelled or remedied.
              </p>
              <Collapse accordion>
                <Collapse.Panel header={renderPostAlarmTitle()}>
                  {renderPostAlarmUsers()}
                </Collapse.Panel>
              </Collapse>
            </div>
          </Tabs.TabPane>
          {/* Emergency Response */}
          <Tabs.TabPane tab="Emergency Response" key="emergencyResponse">
            <div className="emergency-response-settings">
              <p className="normal-label">Emergency Response Provider</p>
              <Form.Item>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select an emergency response provider"
                  value={emergencyProviderId}
                  onChange={setEmergencyProviderId}
                  options={emergencyProviders.map((p) => ({ label: p.name, value: p.id }))}
                />
              </Form.Item>
              <p className="normal-label">Payload Template</p>
              <Form.Item>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Use provider default template"
                  allowClear
                  value={emergencyTemplateId}
                  onChange={setEmergencyTemplateId}
                  options={emergencyTemplates.map((t) => ({ label: t.name, value: t.id }))}
                />
              </Form.Item>
              <p className="normal-label" style={{ color: "#888", fontSize: 12 }}>
                Manage providers and templates from Admin → Emergency Response.
              </p>
            </div>
          </Tabs.TabPane>
        </Tabs>
        
        
      </Form>
    </ConfigureModalSettingWrapper>
  );
};

export default ConfigureModal;